const TRACKING_PAGE_PATH = '/apps/order-tracking';
const TRACKING_RESULT_SELECTOR = '#yq-trackingBox';
const TRACKING_DATE_SELECTOR = '.yq-track-event > .yq-flex-1 > p:first-child';
const TRACKING_MESSAGE_SELECTOR = '.yq-track-event > .yq-flex-1 > p:nth-child(2)';
const TELEPHONE_LINK_MARKER = '__CIELOGAMES_TEL_LINK__';
const LAST_MILE_CARRIER_LABEL = '最後の1マイル運送人';
const DELIVERY_CARRIER_LABEL = 'お届けする配送会社';
const PHONE_NUMBER_PATTERN = /(?:\+?\d(?:[\s().\/-]*\d){4,})/u;
const PHONE_INSTRUCTION_PATTERN =
  /(?:お?電話|ホットライン|コールセンター|お問い合わせ(?!番号)(?:は|先)|ご連絡(?:ください|願います|先)|\bcall(?:\s+us)?\b|\bdial(?:\s+us)?\b|\b(?:contact|reach)\s+(?:us|customer\s+service)\b|\b(?:hotline|telephone|phone|tel)\b|致电|致電|電話|电话|热线|熱線|客服热线|客服熱線|联系电话|聯絡電話|전화|콜센터)/iu;
const PHONE_LEAD_IN_PATTERN =
  /(?:その他.*(?:要件|質問|場合)|ほか.*(?:要件|質問|場合)|ご質問.*(?:場合|は)|ご不明.*(?:場合|点)|お問い合わせ(?!番号).*(?:場合|は)|if\s+you\s+(?:have|need)|for\s+(?:any|other|further|more)\s+(?:questions?|requirements?|assistance|information)|如有|若有|其他.*(?:要求|问题|疑問)|如需|문의.*(?:경우|사항)|도움이\s+필요)/iu;
const DANGLING_PHONE_LEAD_IN_PATTERN =
  /(^|[。.!！?？]\s*)(?:ご質問(?:やご不明点)?がある場合は|ご不明点がある場合は)[、,]?\s*$/u;
const SENTENCE_END_PATTERN = /[。.!！?？\n]\s*$/u;

const splitTrackingClauses = (value) => {
  const clauses = [];
  let clauseStart = 0;

  for (let index = 0; index < value.length; index += 1) {
    const character = value[index];
    const isPeriodInsideNumber =
      character === '.' && /\d/u.test(value[index - 1] || '') && /\d/u.test(value[index + 1] || '');
    const isClauseEnd =
      '。!！?？\n;；,，、'.includes(character) ||
      (character === '.' && !isPeriodInsideNumber);

    if (!isClauseEnd) continue;

    clauses.push(value.slice(clauseStart, index + 1));
    clauseStart = index + 1;
  }

  if (clauseStart < value.length) clauses.push(value.slice(clauseStart));

  return clauses;
};

const normalizeTrackingText = (value) =>
  value
    .replace(/[０-９]/gu, (digit) => String.fromCharCode(digit.charCodeAt(0) - 0xfee0))
    .replace(/[＋／－（）．]/gu, (character) =>
      ({ '＋': '+', '／': '/', '－': '-', '（': '(', '）': ')', '．': '.' })[character],
    );

export const formatJapaneseTrackingDate = (value) => {
  const match = value
    .trim()
    .match(/^(\d{1,2})\s+(\d{1,2})月\s+(\d{4})(?:[,\s]+(\d{1,2}:\d{2}(?::\d{2})?))?$/);

  if (!match) return null;

  const [, day, month, year, time] = match;
  return `${year}年${Number(month)}月${Number(day)}日${time ? ` ${time}` : ''}`;
};

export const formatJapaneseTrackingLabel = (value) =>
  value.trim() === LAST_MILE_CARRIER_LABEL ? DELIVERY_CARRIER_LABEL : null;

export const stripInternationalPhoneInstructions = (value) => {
  const text = value.trim();
  if (!text) return null;

  const clauses = splitTrackingClauses(text).map((clause) => ({
    text: clause,
    hasInstruction:
      PHONE_INSTRUCTION_PATTERN.test(clause) || clause.includes(TELEPHONE_LINK_MARKER),
    hasNumber:
      PHONE_NUMBER_PATTERN.test(normalizeTrackingText(clause)) ||
      clause.includes(TELEPHONE_LINK_MARKER),
    isLeadIn: PHONE_LEAD_IN_PATTERN.test(clause),
    remove: false,
  }));

  clauses.forEach((clause, index) => {
    if (clause.hasInstruction && clause.hasNumber) {
      clause.remove = true;
      return;
    }

    const previousClause = clauses[index - 1];
    const nextClause = clauses[index + 1];
    const sharesPreviousContext =
      Boolean(previousClause) && !SENTENCE_END_PATTERN.test(previousClause.text);
    const sharesNextContext = Boolean(nextClause) && !SENTENCE_END_PATTERN.test(clause.text);
    if (
      (clause.hasInstruction &&
        ((sharesPreviousContext && previousClause.hasNumber) ||
          (sharesNextContext && nextClause.hasNumber))) ||
      (clause.hasNumber &&
        ((sharesPreviousContext && previousClause.hasInstruction) ||
          (sharesNextContext && nextClause.hasInstruction)))
    ) {
      clause.remove = true;
    }
  });

  clauses.forEach((clause, index) => {
    if (!clause.remove) return;

    const previousClause = clauses[index - 1];
    if (
      previousClause?.isLeadIn &&
      !SENTENCE_END_PATTERN.test(previousClause.text)
    ) {
      previousClause.remove = true;
    }

    const nextClause = clauses[index + 1];
    if (nextClause?.isLeadIn && !SENTENCE_END_PATTERN.test(clause.text)) {
      nextClause.remove = true;
    }
  });

  const removedPhoneInstruction = clauses.some((clause) => clause.remove);
  const withoutPhoneInstructions = clauses
    .filter((clause) => !clause.remove)
    .map((clause) => clause.text)
    .join('')
    .replace(new RegExp(TELEPHONE_LINK_MARKER, 'gu'), '');
  const withoutDanglingLeadIn = withoutPhoneInstructions.replace(
    DANGLING_PHONE_LEAD_IN_PATTERN,
    '$1',
  );

  if (!removedPhoneInstruction && withoutDanglingLeadIn === withoutPhoneInstructions) return null;

  return withoutDanglingLeadIn
    .replace(/\s+/gu, ' ')
    .trim()
    .replace(/^[,，、;；]\s*/u, '')
    .replace(/\s*[,，、;；]\s*$/u, '')
    .trim();
};

const replaceTrackingLabels = () => {
  const trackingResult = document.querySelector(TRACKING_RESULT_SELECTOR);
  if (!trackingResult) return;

  const walker = document.createTreeWalker(trackingResult, NodeFilter.SHOW_TEXT);
  let textNode = walker.nextNode();

  while (textNode) {
    const currentText = textNode.nodeValue || '';
    const formattedLabel = formatJapaneseTrackingLabel(currentText);
    if (formattedLabel) {
      textNode.nodeValue = currentText.replace(currentText.trim(), formattedLabel);
      textNode.parentElement?.setAttribute('data-cielogames-label-localized', 'true');
    }
    textNode = walker.nextNode();
  }
};

const removeCarrierPhoneActions = () => {
  document.querySelectorAll('.yq-track-provider use').forEach((iconUse) => {
    const iconName = iconUse.getAttribute('href') || iconUse.getAttribute('xlink:href');
    if (iconName !== '#icon-phone') return;

    const wrapper = iconUse.closest('span');
    if (wrapper) {
      wrapper.remove();
      return;
    }

    iconUse.closest('svg')?.remove();
  });
};

const formatTrackingDates = () => {
  document.querySelectorAll(TRACKING_DATE_SELECTOR).forEach((dateElement) => {
    const formattedDate = formatJapaneseTrackingDate(dateElement.textContent || '');
    if (!formattedDate || formattedDate === dateElement.textContent.trim()) return;

    dateElement.textContent = formattedDate;
    dateElement.dataset.cielogamesDateFormatted = 'true';
  });
};

const removeTrackingPhoneInstructions = () => {
  document.querySelectorAll(TRACKING_MESSAGE_SELECTOR).forEach((messageElement) => {
    const messageClone = messageElement.cloneNode(true);
    messageClone.querySelectorAll('a[href^="tel:"]').forEach((telephoneLink) => {
      telephoneLink.replaceWith(
        `${telephoneLink.textContent || ''} ${TELEPHONE_LINK_MARKER}`,
      );
    });
    const sanitizedMessage = stripInternationalPhoneInstructions(messageClone.textContent || '');

    if (sanitizedMessage === null) return;

    if (!sanitizedMessage) {
      messageElement.closest('.yq-track-event')?.remove();
      return;
    }

    messageElement.textContent = sanitizedMessage;
    messageElement.dataset.cielogamesPhoneInstructionRemoved = 'true';
  });
};

const enhanceTrackingPage = () => {
  removeCarrierPhoneActions();
  removeTrackingPhoneInstructions();
  formatTrackingDates();
  replaceTrackingLabels();
};

const startTrackingPageEnhancements = () => {
  let updateScheduled = false;
  const scheduleUpdate = () => {
    if (updateScheduled) return;

    updateScheduled = true;
    window.requestAnimationFrame(() => {
      updateScheduled = false;
      enhanceTrackingPage();
    });
  };

  enhanceTrackingPage();

  const observer = new MutationObserver(scheduleUpdate);
  observer.observe(document.body, {
    childList: true,
    characterData: true,
    subtree: true,
  });

  window.addEventListener('pagehide', () => observer.disconnect(), { once: true });
};

if (
  typeof window !== 'undefined' &&
  typeof document !== 'undefined' &&
  window.location.pathname.replace(/\/+$/, '').endsWith(TRACKING_PAGE_PATH)
) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startTrackingPageEnhancements, { once: true });
  } else {
    startTrackingPageEnhancements();
  }
}


